/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import {
  DownloadMenu,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  ModalContainer,
  ShareWithOusterModal,
  Shortcut,
  ShortcutAddonKey,
  ShortcutCategory,
  ShortcutKey,
  ShortcutsModal,
  UnshareWithOusterAlert,
  useDisclosure,
  VizTitle,
  AccessLevel,
  Button,
} from '@ouster/webviz-react-components';

import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isMacOs } from 'react-device-detect';
import { TbEyeStar } from 'react-icons/tb';
import { useNavigate, useParams } from 'react-router';

import {
  NavBar,
  NavFullHeightContainer,
  NavFlexRow,
} from '../containers/NavBar';
import useChakraHook from '../hooks/useChakraHook';
import useIsMobileViewport from '../hooks/useIsMobileViewport';
import { useStoreState, useStoreActions } from '../reducers';
import {
  FileInfo,
  OutputFileInfo,
  RecordingPublicityAccessConfig,
} from '../types';
import { BASE_ORG_API, BASE_PUBLISHED_RECORDING_API } from '../utils/constants';
import { useFeedbackForm } from '../utils/customHooks';
import { fetchWithAuth } from '../utils/requests';
import {
  canCopyOusterRecording,
  isAdminLevelRole,
  isOusterSuperAdminRole,
} from '../utils/roles';
import NavPublish from './navBar/NavPublish';
import { PublicDownloadWarning } from './publicSample/PublicDownloadWarning';
import CopyRecordingToOrganizations from './recordingTable/CopyRecordingToOrganizations';
import NavUser from './users/NavUser';
import { driveSensorInfoArrayToRaw } from '../utils/driveUtil';
import RecordingAccessConfig from './recordingTable/RecordingAccessConfig';
import { buildTalkToSalesLink } from '../utils/talkToSales';
import {
  buildDriveNavMenuItems,
  buildDriveVizLink,
  buildDriveVizTitleProps,
  type DriveNavMenuItem,
  type MenuAction,
} from './DriveNavBarHelpers';

type TMenuItem = DriveNavMenuItem & {
  icon: React.ReactElement;
};

/// ///////////////////////////////////////////////////////////////////////////
// Final NavBar
/// ///////////////////////////////////////////////////////////////////////////

const DriveNavBar: React.FC<{ orgId: number }> = ({ orgId }) => {
  const { isPublished, driveInfo, isShared, publicationInfo } = useStoreState(
    (store) => ({
      ...store.drive,
    })
  );
  const { curUserOrg, appUser, user } = useStoreState((store) => ({
    ...store.user,
  }));
  const { deleteDrive, shareWithOuster, loadNewContentStatus } =
    useStoreActions((actions) => actions.drive);
  const { openConfirmDialog } = useStoreActions(
    (storeState) => storeState.confirmDialog
  );
  const { shortcuts } = useStoreState((store) => ({
    ...store.shortcut,
  }));

  const isSampleRecording =
    driveInfo?.accessLevel?.includes('PUBLIC_SAMPLE') ?? false;
  const hasKcToken = Boolean(user?.identity_id || appUser?.identity_id);

  const queryClient = useQueryClient();

  const {
    sourceFileInfo,
    outputForDownload,
    outputForDownloadNonSlam,
    sourceDataForDownload,
    sourceConfigForDownload,
  } = useStoreState((store) => ({ ...store.drive }));
  const downloadableOutputFiles = [
    ...outputForDownload,
    ...outputForDownloadNonSlam,
  ];
  const { addShortcut } = useStoreActions((actions) => actions.shortcut);

  const [sharingRevealed, setSharingRevealed] = useState(false);
  const [downloadRevealed, setDownloadRevealed] = useState(false);
  const [shortcutsRevealed, setShortcutsRevealed] = useState(false);

  const {
    isOpen: isShareModalOpen,
    onOpen: onShareModalOpen,
    onClose: onShareModalClose,
  } = useDisclosure();

  const {
    isOpen: isUnshareAlertOpen,
    onOpen: onUnshareAlertOpen,
    onClose: onUnshareAlertClose,
  } = useDisclosure();

  const {
    isOpen: isDownloadWarningModalOpen,
    onOpen: onDownloadWarningModalOpen,
    onClose: onDownloadWarningModalClose,
  } = useDisclosure();

  const {
    isOpen: isCopyToOrganizationsOpen,
    onOpen: onCopyToOrganizationsOpen,
    onClose: onCopyToOrganizationsClose,
  } = useDisclosure();

  const { showToast } = useChakraHook();
  const navigate = useNavigate();
  const { publishedDriveUuid: publishedDriveUuidRouteParam } = useParams<{
    publishedDriveUuid: string;
  }>();
  const [downloadMenuPosition, setDownloadMenuPosition] = useState({
    top: 0,
    right: 0,
  });
  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  const isMobileViewport = useIsMobileViewport();

  useEffect(() => {
    addShortcut(
      new Shortcut(
        ShortcutCategory.Other,
        [
          // I think this is more usable but we should see how it works on
          // international keyboards like AZERTY
          new ShortcutKey('?', [ShortcutAddonKey.SHIFT], '/'),
          new ShortcutKey('/', [ShortcutAddonKey.CTRL]),
        ],
        () => {
          setShortcutsRevealed((wasRevealed) => !wasRevealed);
        },
        'Toggle Shortcuts'
      )
    );
  }, [addShortcut]);

  /* 
    Control for recording publicity configuration modal
  */
  const {
    isOpen: isPublicitySettingOpen,
    onOpen: onPublicitySettingOpen,
    onClose: onPublicitySettingClose,
  } = useDisclosure();
  const [publicityRecording, setPublicityRecording] = useState<
    RecordingPublicityAccessConfig | undefined
  >(undefined);

  const onConfigureSamples = () => {
    if (!driveInfo) return;
    setPublicityRecording({
      organization_id: driveInfo.organizationId,
      id: Number(driveInfo.id),
      publication_uuid: publicationInfo?.uuid,
      data_category: driveInfo.dataCategory,
      sensor_info: driveSensorInfoArrayToRaw(driveInfo.sensorInfos),
      start_time: driveInfo.startTime === '...' ? null : driveInfo.startTime,
      recording_title: driveInfo.recordingTitle,
    });

    onPublicitySettingOpen();
  };

  const onPublicitySettingCloseHandler = useCallback(() => {
    setPublicityRecording(undefined);
    if (localStorage.getItem('recordingAccessConfigChanged')) {
      queryClient.invalidateQueries({ queryKey: ['shared-from-list'] });
      queryClient.invalidateQueries({ queryKey: ['drive-list'] });

      localStorage.removeItem('recordingAccessConfigChanged');
    }

    onPublicitySettingClose();
  }, [onPublicitySettingClose, queryClient]);

  const onDeleteDrive = () => {
    if (!driveInfo) return;

    const onConfirm = async () => {
      try {
        await deleteDrive({ orgId, driveId: driveInfo.id });

        if (isShared) {
          // If the drive is shared, we need to invalidate the shared list
          // to remove the deleted drive from the list
          queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
        }
        // when an ouster published drive is deleted. invalidate sample data query
        if (
          driveInfo.accessLevel !== AccessLevel.NONE &&
          curUserOrg?.id === 1
        ) {
          queryClient.invalidateQueries({
            queryKey: ['public-public-drive-list'],
          });
          queryClient.invalidateQueries({
            queryKey: ['private-public-drive-list'],
          });

          loadNewContentStatus({ organizationId: curUserOrg.id });
        }

        showToast({
          title: 'Recording deleted',
          description: `Recording deleted, redirecting to recordings page in 3 seconds`,
          status: 'info',
        });
        setTimeout(() => {
          navigate('/drive/private');
        }, 2000);
      } catch (error) {
        showToast({
          title: 'Failed to delete recording',
          description: `${error}`,
          status: 'error',
        });
      }
    };

    // Build bullets at call site so we can include context-specific warnings
    const bullets = [
      'This recording will be permanently deleted and cannot be recovered.',
    ];
    if (isPublished) {
      bullets.push(
        'This recording is published and will be removed from public listings.'
      );
    }
    if (isShared) {
      bullets.push(
        'This recording is shared with Ouster. Deleting it will remove access for the Ouster support team.'
      );
    }

    openConfirmDialog({
      title: 'Delete recording',
      body: 'Are you sure you want to delete this recording?',
      bullets,
      onConfirm,
      isDestructive: true,
      confirmLabel: 'Delete recording',
      cancelLabel: 'Cancel',
    });
  };

  const feedbackFormLink = useFeedbackForm();
  const onOpenFeedbackForm = () => {
    const newWindow = window.open(
      feedbackFormLink,
      '_blank',
      'noopener,noreferrer'
    );
    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const handleDownloadRequest = () => {
    if (hasKcToken || !isSampleRecording) {
      if (downloadButtonRef.current) {
        const buttonRect = downloadButtonRef.current.getBoundingClientRect();
        const scrollY = window.scrollY || document.documentElement.scrollTop;

        setDownloadMenuPosition({
          top: buttonRect.bottom + scrollY + 8,
          right: window.innerWidth - buttonRect.right,
        });
      }

      setDownloadRevealed(true);
    } else {
      onDownloadWarningModalOpen();
    }
  };

  const getFileDownloadUrl = useCallback(
    async (
      driveUuid: string,
      file: FileInfo | OutputFileInfo,
      type: string,
      downloadFilename?: string
    ) => {
      const downloadPath = publishedDriveUuidRouteParam
        ? `${BASE_PUBLISHED_RECORDING_API}/${driveUuid}/${type}/files/${file.id}/signed_url`
        : `${BASE_ORG_API}/${orgId}/recordings/${driveUuid}/${type}/files/${file.id}/signed_url`;

      const response = await fetchWithAuth(downloadPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          downloadFilename: file.filename ?? downloadFilename,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Something went wrong when creating signed URL for file ${file.id}`
        );
      }

      return response.json() as Promise<{ url: string }>;
    },
    [orgId, publishedDriveUuidRouteParam]
  );

  const getMenuItems = (): TMenuItem[] => {
    const canCopyRecording = canCopyOusterRecording(
      curUserOrg?.id,
      curUserOrg?.role,
      driveInfo?.organizationId
    );
    const isAdmin = isAdminLevelRole(curUserOrg?.role);
    const isOusterSuperAdmin = isOusterSuperAdminRole(
      curUserOrg?.id,
      curUserOrg?.role
    );

    const getMenuActionIcon = (action: MenuAction) => {
      switch (action) {
        case 'DOWNLOAD-RECORDING':
          return <Icon icon="download" />;
        case 'COPY-RECORDING-LINK':
          return <Icon icon="share" />;
        case 'COPY-TO-ORGANIZATIONS':
          return <Icon icon="contentCopy" />;
        case 'SHARE-RECORDING':
          return <Icon icon="send" />;
        case 'UNSHARE-RECORDING':
          return <Icon icon="cancelSend" />;
        case 'DELETE-RECORDING':
          return <Icon icon="delete" />;
        case 'SAMPLE-CONFIGURE':
          return <TbEyeStar size={24} />;
        case 'PUBLISH-RECORDING':
          return <Icon icon="public" />;
        case 'UNPUBLISH-RECORDING':
          return <Icon icon="cloudSync" />;
        case 'SEND-FEEDBACK':
          return <Icon icon="feedback" />;
        case 'KEYBOARD-SHORTCUTS':
          return <Icon icon="keyboardAlt" />;
        default:
          return <Icon icon="help" />;
      }
    };

    return buildDriveNavMenuItems({
      canCopyRecording,
      currentOrgId: curUserOrg?.id,
      driveOrgId: driveInfo?.organizationId,
      isShared,
      appUserId: appUser?.id,
      uploadedById: driveInfo?.uploadedById,
      isAdmin,
      isOusterSuperAdmin,
      isPublished,
      driveOrigin: driveInfo?.driveOrigin,
    }).map((item) => ({
      ...item,
      icon: getMenuActionIcon(item.action),
    }));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: 'Link copied to clipboard',
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyCloudVizLink = () => {
    const fullUrl = buildDriveVizLink(
      window.location.origin,
      publishedDriveUuidRouteParam,
      driveInfo?.organizationId,
      driveInfo?.id
    );
    copyToClipboard(fullUrl);
  };

  const handleMenuClick = (item: TMenuItem) => {
    switch (item.action) {
      case 'DOWNLOAD-RECORDING':
        handleDownloadRequest();
        break;
      case 'SHARE-RECORDING':
        onShareModalOpen();
        break;
      case 'UNSHARE-RECORDING':
        onUnshareAlertOpen();
        break;
      case 'DELETE-RECORDING':
        onDeleteDrive();
        break;
      case 'PUBLISH-RECORDING':
      case 'UNPUBLISH-RECORDING':
        setSharingRevealed(true);
        break;
      case 'COPY-RECORDING-LINK':
        copyCloudVizLink();
        break;
      case 'COPY-TO-ORGANIZATIONS':
        onCopyToOrganizationsOpen();
        break;
      case 'SAMPLE-CONFIGURE':
        onConfigureSamples();
        break;
      case 'SEND-FEEDBACK':
        onOpenFeedbackForm();
        break;
      case 'KEYBOARD-SHORTCUTS':
        setShortcutsRevealed(true);
        break;
      default:
        console.warn(`Unsupported action: ${item.action}`);
        break;
    }
  };

  const {
    isOpen: isMenuOpen,
    onClose: onMenuClose,
    onOpen: onMenuOpen,
  } = useDisclosure();

  const onShareRecording = async (notes?: string) => {
    if (!curUserOrg?.id || !driveInfo?.id) return;
    try {
      await shareWithOuster({
        organizationId: curUserOrg.id,
        driveId: driveInfo?.id,
        shareWithOuster: true,
        notes,
      });
      showToast({
        title: 'Recording shared',
        description: `Recording has been successfully shared with Ouster`,
        status: 'info',
      });
      queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
    } catch (error) {
      showToast({
        title: 'Failed to share recording',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  const onUnshareConfirm = () => {
    const unshare = async () => {
      if (!curUserOrg?.id || !driveInfo?.id) return;

      try {
        await shareWithOuster({
          organizationId: curUserOrg.id,
          driveId: driveInfo?.id,
          shareWithOuster: false,
        });
        onUnshareAlertClose();
        showToast({
          title: 'Recording unshared',
          description: `Recording has been unshared with Ouster`,
          status: 'info',
        });
        queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
      } catch (error) {
        showToast({
          title: 'Failed to unshared recording',
          description: `${error}`,
          status: 'error',
        });
      }
    };
    unshare();
  };

  const vizTitleProps = buildDriveVizTitleProps(driveInfo, isShared);

  const talkToSalesLink = buildTalkToSalesLink(
    driveInfo?.recordingTitle || vizTitleProps.text
  );

  return (
    <>
      <NavBar
        data-testid="drive-nav-bar-root"
        centerElement={
          driveInfo && (
            <NavFullHeightContainer
              style={{
                overflow: 'hidden',
                placeItems: 'unset',
                alignItems: 'center',
              }}
            >
              {'recordingTitle' in vizTitleProps ? (
                <VizTitle
                  recordingTitle={vizTitleProps.recordingTitle}
                  details={isMobileViewport ? undefined : vizTitleProps.details}
                />
              ) : (
                <VizTitle text={vizTitleProps.text} />
              )}
            </NavFullHeightContainer>
          )
        }
        rightElement={
          <NavFullHeightContainer>
            <NavFlexRow>
              {isSampleRecording && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const newWindow = window.open(
                      talkToSalesLink,
                      '_blank',
                      'noopener,noreferrer'
                    );
                    if (newWindow) {
                      newWindow.opener = null;
                    }
                  }}
                  data-testid="drive-nav-bar-talk-to-sales-btn"
                >
                  Talk to Sales
                </Button>
              )}
              {!isMobileViewport && (
                <>
                  <IconButton
                    ref={downloadButtonRef}
                    emphasis="subtle"
                    variant="solid"
                    icon="download"
                    onClick={handleDownloadRequest}
                    data-testid="drive-nav-bar-download-btn"
                  />

                  <Menu
                    placement="top-end"
                    offset={[5, 10]}
                    autoSelect={false}
                    onClose={onMenuClose}
                    isOpen={isMenuOpen}
                    data-testid="drive-nav-bar-settings-menu"
                  >
                    <MenuButton
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                      as={IconButton}
                      emphasis="subtle"
                      icon="settings"
                      color="white"
                      onClick={onMenuOpen}
                      data-testid="drive-nav-bar-settings-btn"
                    />
                    <MenuList data-testid="drive-nav-bar-menu-list">
                      {getMenuItems().map((item) => (
                        <MenuItem
                          key={item.action}
                          onClick={() => handleMenuClick(item)}
                          style={{
                            fontSize: 14,
                            paddingBlock: '0.75rem',
                            paddingInline: '1.25rem',
                            marginBlock: '0.25rem',
                            columnGap: '0.5rem',
                          }}
                          _hover={{ bgColor: 'var(--color-blast-off-blue)' }}
                          _focus={{ bgColor: 'var(--color-blast-off-blue)' }}
                          data-testid={`drive-nav-bar-menu-item-${item.action.toLowerCase()}`}
                        >
                          <span style={{ fontSize: 20 }}>{item.icon}</span>
                          {item.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </>
              )}

              {curUserOrg?.id && <NavUser data-testid="drive-nav-bar-user" />}
            </NavFlexRow>
          </NavFullHeightContainer>
        }
      />
      <DownloadMenu
        driveUuid={publishedDriveUuidRouteParam ?? String(driveInfo?.id ?? '')}
        revealed={downloadRevealed}
        sourceDataForDownload={sourceDataForDownload}
        sourceConfigForDownload={sourceConfigForDownload}
        downloadableOutputFiles={downloadableOutputFiles}
        getFileDownloadUrl={getFileDownloadUrl}
        onClickedOutside={() => {
          if (downloadRevealed) {
            setDownloadRevealed(false);
          }
        }}
        recordingTitle={driveInfo?.recordingTitle || undefined}
        popupProps={{
          style: {
            top: downloadMenuPosition.top,
            right: downloadMenuPosition.right,
          },
          'data-testid': 'drive-nav-bar-download-menu',
        }}
      />
      <NavPublish
        revealed={sharingRevealed}
        sourceFilesInfo={sourceFileInfo}
        onClickedOutside={() => {
          if (sharingRevealed) {
            setSharingRevealed(false);
          }
        }}
      />
      <ShortcutsModal
        isOpen={shortcutsRevealed}
        onClose={() => {
          if (shortcutsRevealed) {
            setShortcutsRevealed(false);
          }
        }}
        isMac={isMacOs}
        shortcuts={shortcuts}
      />
      {isShareModalOpen && (
        <ShareWithOusterModal
          isOpen={isShareModalOpen}
          handleClose={onShareModalClose}
          handleShare={onShareRecording}
        />
      )}

      <UnshareWithOusterAlert
        isUnshareAlertOpen={isUnshareAlertOpen}
        onUnshareAlertClose={onUnshareAlertClose}
        onUnshareConfirm={onUnshareConfirm}
      />

      <ModalContainer
        isOpen={isDownloadWarningModalOpen}
        onClose={onDownloadWarningModalClose}
        header="Unlock Sample lidar Downloads"
        footer={null}
        content={<PublicDownloadWarning />}
        data-testid="public-collection-download-warning-modal"
      />

      {driveInfo && (
        <ModalContainer
          isOpen={isCopyToOrganizationsOpen}
          onClose={onCopyToOrganizationsClose}
          header="Copy to Organization(s)"
          footer={null}
          content={
            <CopyRecordingToOrganizations
              driveId={driveInfo.id}
              organizationId={driveInfo.organizationId}
              recordingTitle={driveInfo.recordingTitle}
              onClose={onCopyToOrganizationsClose}
            />
          }
          data-testid="drive-nav-bar-copy-to-organizations-modal"
        />
      )}

      {publicityRecording && (
        <ModalContainer
          isOpen={isPublicitySettingOpen}
          onClose={onPublicitySettingCloseHandler}
          header="Recording Access Configuration"
          footer={null}
          content={
            <RecordingAccessConfig
              recording={publicityRecording}
              onClose={onPublicitySettingCloseHandler}
            />
          }
        />
      )}
    </>
  );
};

export { DriveNavBar };
